/**
 * UN Sustainable Development Goals (SDGs) Constants
 * Official colors and data from: https://www.un.org/sustainabledevelopment/
 */

export interface ISDG {
  number: number;
  title: string;
  shortTitle: string;
  description: string;
  icon_name: string;
  icon: string;
  color: string;
  category: string;
}

export const SDG_GOALS: ISDG[] = [
  {
    number: 1,
    title: 'No Poverty',
    shortTitle: 'No Poverty',
    description: 'End poverty in all its forms everywhere',
    icon_name: 'SDG1Icon',
    icon: '🏚️',
    color: '#E5243B',
    category: 'People'
  },
  {
    number: 2,
    title: 'Zero Hunger',
    shortTitle: 'Zero Hunger',
    description: 'End hunger, achieve food security and improved nutrition and promote sustainable agriculture',
    icon_name: 'SDG2Icon',
    icon: '🌾',
    color: '#DDA63A',
    category: 'People'
  },
  {
    number: 3,
    title: 'Good Health and Well-being',
    shortTitle: 'Good Health',
    description: 'Ensure healthy lives and promote well-being for all at all ages',
    icon_name: 'SDG3Icon',
    icon: '🏥',
    color: '#4C9F38',
    category: 'People'
  },
  {
    number: 4,
    title: 'Quality Education',
    shortTitle: 'Quality Education',
    description: 'Ensure inclusive and equitable quality education and promote lifelong learning opportunities for all',
    icon_name: 'SDG4Icon',
    icon: '📚',
    color: '#C5192D',
    category: 'People'
  },
  {
    number: 5,
    title: 'Gender Equality',
    shortTitle: 'Gender Equality',
    description: 'Achieve gender equality and empower all women and girls',
    icon_name: 'SDG5Icon',
    icon: '♀️',
    color: '#FF3A21',
    category: 'People'
  },
  {
    number: 6,
    title: 'Clean Water and Sanitation',
    shortTitle: 'Clean Water',
    description: 'Ensure availability and sustainable management of water and sanitation for all',
    icon_name: 'SDG6Icon',
    icon: '💧',
    color: '#26BDE2',
    category: 'Planet'
  },
  {
    number: 7,
    title: 'Affordable and Clean Energy',
    shortTitle: 'Clean Energy',
    description: 'Ensure access to affordable, reliable, sustainable and modern energy for all',
    icon_name: 'SDG7Icon',
    icon: '⚡',
    color: '#FCC30B',
    category: 'Prosperity'
  },
  {
    number: 8,
    title: 'Decent Work and Economic Growth',
    shortTitle: 'Decent Work',
    description: 'Promote sustained, inclusive and sustainable economic growth, full and productive employment and decent work for all',
    icon_name: 'SDG8Icon',
    icon: '💼',
    color: '#A21942',
    category: 'Prosperity'
  },
  {
    number: 9,
    title: 'Industry, Innovation and Infrastructure',
    shortTitle: 'Innovation',
    description: 'Build resilient infrastructure, promote inclusive and sustainable industrialization and foster innovation',
    icon_name: 'SDG9Icon',
    icon: '🏭',
    color: '#FD6925',
    category: 'Prosperity'
  },
  {
    number: 10,
    title: 'Reduced Inequalities',
    shortTitle: 'Reduced Inequalities',
    description: 'Reduce inequality within and among countries',
    icon_name: 'SDG10Icon',
    icon: '⚖️',
    color: '#DD1367',
    category: 'Prosperity'
  },
  {
    number: 11,
    title: 'Sustainable Cities and Communities',
    shortTitle: 'Sustainable Cities',
    description: 'Make cities and human settlements inclusive, safe, resilient and sustainable',
    icon_name: 'SDG11Icon',
    icon: '🏙️',
    color: '#FD9D24',
    category: 'Prosperity'
  },
  {
    number: 12,
    title: 'Responsible Consumption and Production',
    shortTitle: 'Responsible Consumption',
    description: 'Ensure sustainable consumption and production patterns',
    icon_name: 'SDG12Icon',
    icon: '♻️',
    color: '#BF8B2E',
    category: 'Planet'
  },
  {
    number: 13,
    title: 'Climate Action',
    shortTitle: 'Climate Action',
    description: 'Take urgent action to combat climate change and its impacts',
    icon_name: 'SDG13Icon',
    icon: '🌍',
    color: '#3F7E44',
    category: 'Planet'
  },
  {
    number: 14,
    title: 'Life Below Water',
    shortTitle: 'Life Below Water',
    description: 'Conserve and sustainably use the oceans, seas and marine resources for sustainable development',
    icon_name: 'SDG14Icon',
    icon: '🐟',
    color: '#0A97D9',
    category: 'Planet'
  },
  {
    number: 15,
    title: 'Life on Land',
    shortTitle: 'Life on Land',
    description: 'Protect, restore and promote sustainable use of terrestrial ecosystems, sustainably manage forests, combat desertification, and halt and reverse land degradation and halt biodiversity loss',
    icon_name: 'SDG15Icon',
    icon: '🌲',
    color: '#56C02B',
    category: 'Planet'
  },
  {
    number: 16,
    title: 'Peace, Justice and Strong Institutions',
    shortTitle: 'Peace & Justice',
    description: 'Promote peaceful and inclusive societies for sustainable development, provide access to justice for all and build effective, accountable and inclusive institutions at all levels',
    icon_name: 'SDG16Icon',
    icon: '☮️',
    color: '#00689D',
    category: 'Peace'
  },
  {
    number: 17,
    title: 'Partnerships for the Goals',
    shortTitle: 'Partnerships',
    description: 'Strengthen the means of implementation and revitalize the Global Partnership for Sustainable Development',
    icon_name: 'SDG17Icon',
    icon: '🤝',
    color: '#19486A',
    category: 'Partnership'
  }
];

/**
 * Get SDG by number (1-17)
 */
export function getSDGByNumber(number: number): ISDG | undefined {
  return SDG_GOALS.find(sdg => sdg.number === number);
}

/**
 * Get multiple SDGs by numbers
 */
export function getSDGsByNumbers(numbers: number[]): ISDG[] {
  return SDG_GOALS.filter(sdg => numbers.includes(sdg.number));
}

/**
 * Get SDG color by number
 */
export function getSDGColor(number: number): string {
  const sdg = getSDGByNumber(number);
  return sdg?.color || '#000000';
}

/**
 * Get SDG title by number
 */
export function getSDGTitle(number: number): string {
  const sdg = getSDGByNumber(number);
  return sdg?.title || `SDG ${number}`;
}

/**
 * Format SDG numbers for display (e.g., "SDG 1, 7, 13")
 */
export function formatSDGNumbers(numbers: number[]): string {
  if (!numbers || numbers.length === 0) return '';
  const sorted = [...numbers].sort((a, b) => a - b);
  return `SDG ${sorted.join(', ')}`;
}

/**
 * Group bonds or items by SDG numbers
 */
export function groupBySDG<T extends { sdg_numbers?: number[] }>(items: T[]): Map<number, T[]> {
  const groups = new Map<number, T[]>();

  items.forEach(item => {
    if (item.sdg_numbers) {
      item.sdg_numbers.forEach(sdgNumber => {
        if (!groups.has(sdgNumber)) {
          groups.set(sdgNumber, []);
        }
        groups.get(sdgNumber)!.push(item);
      });
    }
  });

  return groups;
}

/**
 * SDG Icons mapping for lazy loading
 * Icons should be created as separate React components in components/icons/sdg/
 */
export const SDG_ICON_COMPONENTS: Record<string, string> = {
  SDG1Icon: 'SDG1Icon',
  SDG2Icon: 'SDG2Icon',
  SDG3Icon: 'SDG3Icon',
  SDG4Icon: 'SDG4Icon',
  SDG5Icon: 'SDG5Icon',
  SDG6Icon: 'SDG6Icon',
  SDG7Icon: 'SDG7Icon',
  SDG8Icon: 'SDG8Icon',
  SDG9Icon: 'SDG9Icon',
  SDG10Icon: 'SDG10Icon',
  SDG11Icon: 'SDG11Icon',
  SDG12Icon: 'SDG12Icon',
  SDG13Icon: 'SDG13Icon',
  SDG14Icon: 'SDG14Icon',
  SDG15Icon: 'SDG15Icon',
  SDG16Icon: 'SDG16Icon',
  SDG17Icon: 'SDG17Icon'
};

// Export all SDG numbers as a constant array for iteration
export const ALL_SDG_NUMBERS = Array.from({ length: 17 }, (_, i) => i + 1);

// Export SDG categories for grouping
export const SDG_CATEGORIES = {
  PEOPLE: [1, 2, 3, 4, 5],      // End poverty and hunger, health, education, gender equality
  PLANET: [6, 12, 13, 14, 15],  // Water, responsible consumption, climate, life below water and on land
  PROSPERITY: [7, 8, 9, 10, 11], // Energy, work, innovation, reduced inequalities, sustainable cities
  PEACE: [16],                   // Peace, justice and strong institutions
  PARTNERSHIP: [17]              // Partnerships for the goals
};

// Category array for iteration
export const SDG_CATEGORY_LIST = [
  { name: 'People', color: '#E5243B', sdgs: SDG_CATEGORIES.PEOPLE, description: 'End poverty and hunger, ensure health, education, and gender equality' },
  { name: 'Planet', color: '#4C9F38', sdgs: SDG_CATEGORIES.PLANET, description: 'Protect natural resources, climate, and ecosystems' },
  { name: 'Prosperity', color: '#FF3A21', sdgs: SDG_CATEGORIES.PROSPERITY, description: 'Ensure prosperous and fulfilling lives in harmony with nature' },
  { name: 'Peace', color: '#26BDE2', sdgs: SDG_CATEGORIES.PEACE, description: 'Foster peaceful, just, and inclusive societies' },
  { name: 'Partnership', color: '#19486A', sdgs: SDG_CATEGORIES.PARTNERSHIP, description: 'Implement the agenda through global partnership' }
];

export default SDG_GOALS;